import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

import Navbar from './components/Navbar';
import Home from './pages/Home';
import ContactAdmin from './pages/ContactAdmin';
import StickyContactButton from './components/StickyContactButton';
import Checkout from './pages/Checkout';
import LiveClasses from './pages/LiveClasses';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Learn from './pages/Learn';
import Audience from './pages/Audience';
import Workshops from './pages/Workshops';
import { audiences } from './data/audiences';
import Login from './pages/Login/Login';
import ProgramCheckout from './pages/Billing/ProgramCheckout';
import { AuthProvider } from './context/AuthContext';
import './App.css';

// Interview Prep carries a large question bank, so it loads as its own chunk
const InterviewPrep = lazy(() => import('./pages/InterviewPrep'));

// Main app layout and routing container
const AppContent = () => {
  const location = useLocation();

  // Start each new page at the top; the home page handles its own #section links
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  const isContactAdminPage = location.pathname === '/admin-dashboard';
  const isLoginPage = location.pathname === '/login';
  // Session checkout is a focused page with its own slim header
  const isSessionCheckout = (location.pathname === '/checkout' && new URLSearchParams(location.search).has('booking'))
    || location.pathname.startsWith('/billing/');

  return (
    <div className="App">
      {/* Hide navbar on login page, admin dashboard and session checkout */}
      {!isContactAdminPage && !isLoginPage && !isSessionCheckout && <Navbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin-dashboard" element={<ContactAdmin />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/billing/:program" element={<ProgramCheckout />} />
        <Route path="/learn" element={<Learn />} />
        <Route path="/learn/:tech" element={<Learn />} />
        <Route path="/learn/:tech/:lesson" element={<Learn />} />
        <Route path="/interview-prep" element={<Suspense fallback={<div className="page" />}><InterviewPrep /></Suspense>} />
        <Route path="/interview-prep/:track" element={<Suspense fallback={<div className="page" />}><InterviewPrep /></Suspense>} />
        <Route path="/interview-prep/:track/:question" element={<Suspense fallback={<div className="page" />}><InterviewPrep /></Suspense>} />
        {audiences.map((a) => (
          <Route key={a.slug} path={`/${a.slug}`} element={<Audience slug={a.slug} />} />
        ))}
        <Route path="/workshops" element={<Workshops />} />
        <Route path="/live-classes" element={<LiveClasses />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
      </Routes>
      {/* Hide sticky contact button on login page and session checkout */}
      {!isLoginPage && !isSessionCheckout && <StickyContactButton />}
    </div>
  );
};

// Root entry point providing routing context
const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </BrowserRouter>
);

export default App;
